import { ref, computed, watch, onMounted, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import type { StyleProps } from './use-section-props'

/**
 * 图片配置接口
 */
export interface ImageConfig {
  width: number
  height: number
  url: string
  multiple: number
}

const emptyImageConfig = (): ImageConfig => ({
  width: 0,
  height: 0,
  url: '',
  multiple: 3,
})

/**
 * 处理图片配置的 composable
 */
export function useSectionImage(mergedStyles: ComputedRef<StyleProps>) {
  const route = useRoute()

  const imageConfig = ref<ImageConfig>(emptyImageConfig())

  /**
   * bgI 会变化时（如先兜底再换真实头图）要重算，避免 imageConfig 写死；
   * 同时必须随 route.meta.assetsMap 就绪而重算：仅 watch bgI 时，首次在 setup
   * 执行早于 meta 挂 assetsMap，之后 bgI 未变则永不解析，全榜只剩 key/空白。
   */
  const syncFromAssetsMap = () => {
    if (!route.meta?.assetsMap) {
      imageConfig.value = emptyImageConfig()
      return
    }
    const key = mergedStyles.value.bgI
    if (key == null || String(key).trim() === '') {
      imageConfig.value = emptyImageConfig()
      return
    }
    const keyStr = String(key)
    const asset = (
      route.meta.assetsMap as Record<
        string,
        {
          height: number
          size: string
          type: string
          url: string
          width: number
          multiple: number
        }
      >
    )?.[keyStr]
    if (asset) {
      imageConfig.value.width = Number(asset.width)
      imageConfig.value.height = Number(asset.height)
      imageConfig.value.url = asset.url
      imageConfig.value.multiple = asset.multiple
    } else {
      imageConfig.value = emptyImageConfig()
    }
  }

  watch(
    () => [mergedStyles.value.bgI, route.meta?.assetsMap] as const,
    () => {
      syncFromAssetsMap()
    },
    { immediate: true }
  )

  onMounted(() => {
    syncFromAssetsMap()
  })

  return {
    imageConfig: computed(() => imageConfig.value),
  }
}
